import {
  CalendarClockIcon,
  CircleDollarSignIcon,
  ClockIcon,
  CopyIcon,
  LinkIcon,
  MapPinIcon,
  SearchIcon,
  SunMoonIcon,
} from "lucide-react";

import { RichTextDisplayArea } from "./RichTextDisplayArea";
import { Column, Row, Text } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useGetCompanyInformation } from "@/hooks/queries/useGetCompanyInformation";
import { employmentTypeToLabel } from "@/lib/constants";
import { misc } from "@/lib/misc";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import { CompensationType, Job, ShiftType } from "@/types";

// Chip-sized shift labels; shiftTypeToLabel ("First shift") is too long here.
const shiftTypeToShortLabel: Record<ShiftType, string> = {
  [ShiftType.FIRSTSHIFT]: "1st",
  [ShiftType.SECONDSHIFT]: "2nd",
  [ShiftType.THIRDSHIFT]: "3rd",
  [ShiftType.WEEKEND]: "Weekend",
};

export const JobOverview = ({
  className,
  companyName,
  headerAction,
  hideActions,
  job,
  screenName,
}: {
  className?: string;
  companyName: string;
  headerAction?: React.ReactNode;
  hideActions?: boolean;
  job: Job;
  screenName: MIXPANEL_SCREENS;
}) => {
  const { data: companyInformation } = useGetCompanyInformation();
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  const companyInfoPayload =
    companyInformation?.companyInformation?.companyInformation;
  const companyAddress = companyInfoPayload?.address ?? null;
  const companyDescription = companyInfoPayload?.description ?? null;
  const companyWebsiteUrl = companyInfoPayload?.websiteUrl ?? null;

  const handleCopyPublicJobLink = async () => {
    try {
      mixpanelUtils.trackButtonClick("Copy Public Job Link", screenName);
      const jobLink = `https://app.laborup.com/laborup-jobs/${job.gcpTalentId}`;
      await navigator.clipboard.writeText(jobLink);
      successToast({ description: "Public job link copied to clipboard!" });
    } catch {
      errorToast({ description: "Failed to copy to clipboard" });
    }
  };

  const handleCopyJobInfo = async () => {
    try {
      mixpanelUtils.trackButtonClick("Copy Job Info", screenName);
      const text = misc.buildClipboardTextForJobProfile({
        companyAddress,
        companyDescription,
        companyName,
        companyWebsiteUrl,
        job,
      });
      await navigator.clipboard.writeText(text);
      successToast({ description: "Copied job info to clipboard!" });
    } catch {
      errorToast({ description: "Failed to copy to clipboard" });
    }
  };

  const chipClassName =
    "flex items-center gap-1 rounded-md border-slate-200 bg-white px-2.5 py-1 font-normal text-slate-700";

  // Always shift order, not the order the job stored them in.
  const shiftsLabel = (Object.keys(shiftTypeToShortLabel) as ShiftType[])
    .filter((shiftType) => job.shiftType.includes(shiftType))
    .map((shiftType) => shiftTypeToShortLabel[shiftType])
    .join(", ");

  // One list; the search icon marks the keywords that seed Search V2. The rest
  // only highlight matches on candidate profiles.
  const renderKeywords = () => {
    if (!job.keywords.length) {
      return null;
    }

    return (
      <Column className="mb-10 gap-2">
        <Label className="text-base font-semibold leading-normal text-slate-800">
          Keywords
        </Label>
        <Row className="flex-wrap gap-2">
          {job.keywords.map((keyword) => (
            <Badge className={chipClassName} key={keyword.keyword}>
              {keyword.isDefaultSearchKeyword ? (
                <SearchIcon className="h-3.5 w-3.5 text-slate-500" />
              ) : null}
              {keyword.keyword}
            </Badge>
          ))}
        </Row>
      </Column>
    );
  };

  const render = () => {
    return (
      <Column className={cn("gap-6", className)}>
        <Row className="items-center justify-between gap-4">
          <Row className="no-scrollbar min-w-0 items-center gap-2 overflow-x-auto [&>*]:shrink-0">
            <Badge className={chipClassName}>
              <ClockIcon className="h-4 w-4 text-slate-500" />
              {employmentTypeToLabel[job.employmentType]}
            </Badge>
            <Badge className={chipClassName}>
              <MapPinIcon className="h-4 w-4 text-slate-500" />
              {job.city}, {job.state}
            </Badge>
            <Badge className={chipClassName}>
              <CircleDollarSignIcon className="h-4 w-4 text-slate-500" />$
              {job.compensationMin} - ${job.compensationMax}{" "}
              {job.compensationType === CompensationType.HOURLY
                ? "per hour"
                : "per year"}
            </Badge>
            {shiftsLabel ? (
              <Badge className={chipClassName}>
                <SunMoonIcon className="h-4 w-4 text-slate-500" />
                {shiftsLabel}
              </Badge>
            ) : null}
            {job.shiftStart && job.shiftEnd ? (
              <Badge className={chipClassName}>
                <CalendarClockIcon className="h-4 w-4 text-slate-500" />
                {misc.formatShiftTime(job.shiftStart)} -{" "}
                {misc.formatShiftTime(job.shiftEnd)}
              </Badge>
            ) : null}
          </Row>
          {headerAction}
          {!hideActions && (
            <Row className="shrink-0 gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="rounded-full"
                    onClick={() => void handleCopyPublicJobLink()}
                    size="sm"
                    variant="outlineSecondary"
                  >
                    <LinkIcon className="h-4 w-4" />
                    Copy Public Job Link
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Copy public job link to clipboard
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="rounded-full"
                    onClick={() => void handleCopyJobInfo()}
                    size="sm"
                    variant="outlineSecondary"
                  >
                    <CopyIcon className="h-4 w-4" />
                    Copy Job Info
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Copy job and company info to clipboard
                </TooltipContent>
              </Tooltip>
            </Row>
          )}
        </Row>
        <Column className="gap-6">
          <RichTextDisplayArea label="Job Summary" richText={job.overview} />
          <RichTextDisplayArea
            label="Responsibilities"
            richText={job.responsibilities}
          />
          <RichTextDisplayArea
            label="Qualifications"
            richText={job.qualifications}
          />
          <RichTextDisplayArea
            label="Benefits"
            richText={job.benefits ?? "<p>No benefits</p>"}
          />
          <Column className="gap-2">
            <Label className="text-base font-semibold leading-normal text-slate-800">
              Customer&apos;s Top Priority
            </Label>
            <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-5">
              <Text className="text-sm font-normal leading-relaxed text-[#121212] dark:text-[#FAFAFA]">
                {job.topCustomerPriority ?? "No priority specified"}
              </Text>
            </div>
          </Column>
          {renderKeywords()}
        </Column>
      </Column>
    );
  };

  return render();
};
